import { Box, Link, Theme, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import ReactMarkdown, { uriTransformer } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeSanitize from 'rehype-sanitize';
import { getImage, ImageSize } from '@shared/utils/imageFormat';
import { isLocalUrl } from '@/lib/utils/DataFormat';
import Mustache from 'mustache';

export interface IProps {
  text: string;
  templateData?: any;
}

const useStyles = (theme: Theme) => ({
  image: {
    maxWidth: '100% !important',
  },
  content: {
    '& blockquote': {
      borderWidth: '0px !important',
      borderLeftWidth: '8px !important',
      borderStyle: 'solid !important',
      backgroundColor: `${theme.palette.background.shadedown_10} !important`,
      borderColor: `${theme.palette.background.shadedown_20} !important`,
      margin: '0px !important',
      padding: '1px 10px !important',
    },
    '& pre': {
      backgroundColor: `${theme.palette.background.shadedown_10} !important`,
      padding: '4px !important',
    },
    '& table': {
      margin: '8px 0 !important',
      borderStyle: 'solid !important',
      borderWidth: '1px !important',
      borderColor: `${theme.palette.background.shadedown_20} !important`,
      borderCollapse: 'collapse !important',
    },
    '& th': {
      backgroundColor: `${theme.palette.background.shadedown_20} !important`,
      padding: '3px !important',
    },
    '& td': {
      borderStyle: 'solid !important',
      borderWidth: '1px !important',
      borderColor: `${theme.palette.background.shadedown_20} !important`,
      padding: '3px !important',
    },
    '& .task-list-item': {
      listStyleType: 'none !important',
      marginLeft: '-20px !important',
    },
  },
  linkItem: {
    textDecoration: 'none !important',
    fontSize: '16px !important',
    '&:hover': {
      textDecoration: 'underline !important',
      color: '##78AF36 !important',
    },
  },
});

// const img_default_width = 600;
// const coeoImageStart = `https://${process.env.STATIC_IMAGES}`;
// Only allow coeo images
const userInputImageUri = (src: string, alt: string, title: string) => {
  if (src.indexOf('/') >= 0) {
    return '/images/no_external_images.png';
  } else {
    return uriTransformer(getImage(src, ImageSize.Default));
  }
};

const headerMap = (props: any) => {
  return <Typography variant={props.node.tagName} {...props} />;
};
export const MarkdownRender = React.memo(
  ({ text, templateData }: IProps) => {
    const theme = useTheme();
    const classes = useStyles(theme);

    const sx = {
      image: classes.image,
    };

    const configs = {
      trusted: {
        linkTarget: '_blank',
      },
      userInput: {
        linkTarget: '_blank',
        components: {
          a: ({
            href,
            target,
            ...props
          }: {
            href: string;
            target: string;
            [x: string]: any;
          }) => {
            return (
              <Link
                sx={classes.linkItem}
                target={isLocalUrl(href) ? '_self' : '_blank'}
                href={href}
                {...props}
              />
            );
          },
          img: (props: any) => <Box component="img" sx={sx.image} {...props} />,
          h1: headerMap,
          h2: headerMap,
          h3: headerMap,
          h4: headerMap,
          h5: headerMap,
          h6: headerMap,
        },
      },
    };
    let renderText;
    if (templateData) {
      try {
        renderText = Mustache.render(text, templateData);
      } catch (e) {
        renderText = text;
      }
    } else {
      renderText = text;
    }
    return (
      <Box sx={classes.content}>
        <ReactMarkdown
          children={renderText}
          linkTarget="_blank" // (href, children, title) => string
          // style={{ minHeight: '500px' }}
          components={configs['userInput'].components}
          remarkPlugins={[remarkGfm, remarkBreaks]}
          rehypePlugins={[rehypeSanitize]}
          transformImageUri={userInputImageUri}
          imageAccept={'.jpg,.jpeg,.png'}
          // allowedElements={['p', 'strong', 'em']}
        />
      </Box>
    );
  },
  (prevProps, nextProps) => prevProps.text === nextProps.text
);

MarkdownRender.displayName = 'MarkdownRender';
